import React, { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { gql } from '@soundxyz/gql-string';
import { styled } from '@components-shared/src/stitches';
import { CollectorsSort, CollectorsSortChips } from '@components/Collectors';
import { CollectorCard } from '@components/Collectors/Cards/CollectorCard';
import { ViewCommunityDropdown } from '@components/basics/Dropdowns/ViewCommunityDropdown';
import { useAuth } from '@context/auth/base';
import { ParamsStore, useParams } from '@context/params';

import type { FragmentType } from '@graphql/reactQuery';
import {
  AllArtistCollectorsDocument,
  CollectorsSectionArtistInfoFragmentDoc,
  getFragment,
  useInfiniteQuery,
} from '@graphql/reactQuery';
import { useSafeLayoutEffect } from '@hooks/useSafeLayoutEffect';
import { useStableCallback } from '@hooks/useStableCallback';
import { SectionHeader } from './SectionHeader';

/*#__PURE__*/ gql(/* GraphQL */ `
  query AllArtistCollectors($artistId: UUID!, $pagination: ArtistCollectorCursorConnectionArgs!) {
    artist(id: $artistId) {
      id
      collectors(pagination: $pagination) {
        pageInfo {
          hasNextPage
          endCursor
        }
        edges {
          node {
            id
            nftsCount
            firstNftCollectedDate
            ...CollectorCardInfo
          }
          cursor
        }
      }
    }
  }

  fragment CollectorsSectionArtistInfo on Artist {
    id
    webappUri
    user {
      id
      publicAddress
    }
  }
`);

export const CollectorsSection = ({
  artist: artistFragment,
}: {
  artist: FragmentType<CollectorsSectionArtistInfoFragmentDoc>;
}) => {
  const artist = getFragment(CollectorsSectionArtistInfoFragmentDoc, artistFragment);
  const artistId = artist.id;

  const [sort, setSort] = React.useState<CollectorsSort>(CollectorsSort.TOP);

  const { sort: windowQuerySort } = useParams();

  useSafeLayoutEffect(() => {
    if (windowQuerySort != null) {
      if (windowQuerySort === CollectorsSort.EARLIEST) setSort(CollectorsSort.EARLIEST);
      else {
        ParamsStore.sort = null;
      }
    }
  }, [windowQuerySort]);

  const [bottomRef, isAtBottom] = useInView({
    threshold: 0.1,
  });

  const {
    orderedList: collectors,
    loadMoreNextPage: loadMore,
    hasNextPage = false,
    isLoading,
  } = useInfiniteQuery(AllArtistCollectorsDocument, {
    filterQueryKey: {
      artistId,
      sort,
    },
    getNextPageParam({ data: { artist } }) {
      return (
        artist?.collectors.pageInfo.hasNextPage && { after: artist.collectors.pageInfo.endCursor }
      );
    },
    variables({ pageParam }) {
      return {
        artistId,
        pagination: {
          sort:
            sort === 'top'
              ? { nftsCount: 'DESC' as const }
              : { firstNftCollectedDate: 'ASC' as const },
          first: 40,
          after: pageParam?.after || null,
        },
      };
    },
    list({ artist }) {
      return artist?.collectors.edges.map(edge => edge.node);
    },
    uniq({ id }) {
      return id;
    },
    staleTime: 0,
  });

  useEffect(() => {
    if (isAtBottom && hasNextPage) {
      loadMore();
    }
  }, [isAtBottom, loadMore, hasNextPage]);

  const onSetSort = useStableCallback((newSort: CollectorsSort) => {
    if (sort !== newSort) {
      setSort(newSort);

      switch (newSort) {
        case CollectorsSort.TOP: {
          ParamsStore.sort = null;
          break;
        }
        case CollectorsSort.EARLIEST: {
          ParamsStore.sort = newSort;
          break;
        }
        default: {
          break;
        }
      }
    }
  });

  const { authUser } = useAuth();
  const isOwner = authUser?.id === artist?.user?.id;

  return (
    <Container>
      <SectionHeader header="Collectors" userWebappUri={artist.webappUri} />
      <div className="mt-2 flex w-full  justify-between px-6 font-base text-base-l text-neutral400 lt:px-0">
        <CollectorsSortChips sort={sort} setSort={onSetSort} />
        {isOwner && <ViewCommunityDropdown align="end" />}
      </div>
      <CollectorsContainer>
        {collectors?.map((collector, index) => (
          <CollectorCard
            key={collector.id}
            collector={collector}
            index={index + 1}
            sort={sort}
            variant="collector"
            loading={isLoading}
            seeAllPage
          />
        ))}
        <div ref={bottomRef} />
      </CollectorsContainer>
    </Container>
  );
};

export const Container = styled('div', {
  marginBottom: 64,
  paddingBottom: 0,

  '@tablet': {
    paddingBottom: 64,
  },
  '@laptop': {
    paddingBottom: 0,
  },
});

export const CollectorsContainer = styled('div', {
  width: '100%',
  display: 'grid',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  paddingX: 24,
  marginTop: 24,
  gap: 20,
  gridTemplateColumns: '1fr',
  '@tablet': {
    marginTop: 20,
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
  '@tabletLandscape': {
    paddingX: 24,
    gridTemplateColumns: 'repeat(3, 1fr)',
  },
  '@laptop': {
    paddingX: 0,
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
  },
  '@desktop': {
    paddingX: 0,
    gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
  },
});
